/**
 * Asset API
 *
 * Typed client for the channel-scoped asset store (/api/assets). Custom-HTML
 * overlays reference these by their public URL (served at /media/{id}).
 *
 * Upload is multipart/form-data, so it bypasses the JSON `api` client and posts
 * with fetch directly (attaching the same JWT). List/delete use the JSON client.
 */

import api from '@/api/client'

export type AssetKind = 'image' | 'sound' | 'font'

export interface AssetInfo {
  id: string
  channel_id: number
  kind: AssetKind
  filename: string
  content_type: string
  size_bytes: number
  /** Public URL to reference in overlay html/css/js. */
  url: string
}

/** List a channel's assets (optionally filtered by kind). */
export function listAssets(channelId: number, kind?: AssetKind): Promise<AssetInfo[]> {
  const q = kind ? `&kind=${kind}` : ''
  return api.get<AssetInfo[]>(`/api/assets?channel_id=${channelId}${q}`)
}

/** Delete an asset (bytes + row). */
export function deleteAsset(assetId: string): Promise<void> {
  return api.delete<void>(`/api/assets/${assetId}`)
}

/**
 * Upload an asset (multipart). Returns the stored asset incl. its public URL.
 * Mirrors the shared client's auth + base-URL handling, but with FormData so the
 * browser sets the multipart boundary (we must NOT force application/json here).
 */
export async function uploadAsset(
  channelId: number,
  kind: AssetKind,
  file: File,
): Promise<AssetInfo> {
  const base = import.meta.env.VITE_API_URL || ''
  const token = localStorage.getItem('pratbot_token')
  const form = new FormData()
  form.append('file', file)

  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // Deliberately no Content-Type — the browser adds the multipart boundary.

  const res = await fetch(
    `${base}/api/assets?channel_id=${channelId}&kind=${kind}`,
    { method: 'POST', headers, body: form },
  )
  if (!res.ok) {
    let detail = res.statusText
    try {
      const data = await res.json()
      if (data?.detail) detail = data.detail
    } catch {
      // not JSON
    }
    throw new Error(detail)
  }
  return res.json()
}
